import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import {
  FlexBox,
  Typography,
  Backdrop,
  DotProgress,
  SelectDropdown,
  color,
  FormDialog,
  Box
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { AttractLoop, actions as configActions, selectors as configSelectors } from 'store/configurations';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { getSortedList } from 'utils/stringFormat';
import Controls from '../components/Controls';
import LinkedContentTable from '../components/LinkedContentTable';
import ManageContentUrlOverlay from '../components/ManageContentUrlOverlay';
import ManageImageOverlay from '../components/ManageImageOverlay';
import ManageVideoOverlay from '../components/ManageVideoOverlay';
import MediaTable from '../components/MediaTable';
import PreviewImageOverlay from '../components/PreviewImageOverlay';
import TransitionTimeOverlay from '../components/TransitionTimeOverlay';
import { MediaType, MediaResponse, MediaTypes, MediaConfigurationProps } from '../types';
import { getMediaTypeLabel } from '../utils';

const AttractLoopLayout = (props: MediaConfigurationProps) => {
  const { configuration } = props;
  const { id, configuration: configColumn } = configuration;

  const globalSettings = get(configColumn, 'globalSettings');
  const attractLoop = get(globalSettings, 'attractLoop');

  const mediaTypeList: MediaType[] = [
    { name: 'Slideshow', value: 'attract-image' },
    { name: 'Video', value: 'attract-video' },
    { name: 'Linked Content', value: 'attract-url' }
  ];
  let defaultMedia = mediaTypeList[0];
  if (attractLoop && attractLoop.type === MediaTypes.ATTRACT_IMAGE) defaultMedia = mediaTypeList[0];
  if (attractLoop && attractLoop.type === MediaTypes.ATTRACT_VIDEO) defaultMedia = mediaTypeList[1];
  if (attractLoop && attractLoop.type === MediaTypes.LINKED_CONTENT) defaultMedia = mediaTypeList[2];

  const [updateConfiguration, resetConfigAction, resetAction, getMedia, deleteMedia, downloadMedia] = useActions([
    configActions.updateConfiguration,
    configActions.resetAction,
    mediaActions.resetAction,
    mediaActions.getMedia,
    mediaActions.deleteMedia,
    mediaActions.downloadMedia
  ]);

  const mediaRecords = useSelector(mediaSelectors.selectAttractImages) || [];
  const { error, pending, response, successful } = useSelector(mediaSelectors.selectAction);
  const {
    success: configSuccess,
    pending: configPending,
    error: configError
  } = useSelector(configSelectors.selectAction);

  const [addParams, setAddParams] = useState<boolean>(false);
  const [deleteParams, setDeleteParams] = useState<MediaResponse | boolean | null>(null);
  const [downloadParams, setDownloadParams] = useState<MediaResponse | null>(null);
  const [editParams, setEditParams] = useState<MediaResponse | boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [imagePreviewSrc, setImagePreviewSrc] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<MediaType>(defaultMedia);
  const [showTransitionTimeOverlay, setShowTransitionTimeOverlay] = useState<boolean>(false);

  const mediaTypeLabelLowercase = getMediaTypeLabel(mediaType.value as MediaTypes, true);

  const sortedMediaRecords = useMemo(() => {
    mediaRecords.forEach((item) => {
      if (attractLoop && attractLoop.order) {
        let priority = attractLoop.order.indexOf(item.s3Name);
        if (priority === -1) priority = attractLoop.order.length;
        item.priority = priority;
      }
    });
    return mediaRecords.sort((a, b) => (a.priority || 0) - (b.priority || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaRecords, attractLoop && attractLoop.order]);

  const refreshMedia = () => {
    if (mediaType && mediaType.value && mediaType.value !== 'attract-url') {
      getMedia({ type: mediaType.value, configurationId: id });
    }
  };

  useEffect(() => {
    refreshMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mediaType]);

  useEffect(() => {
    if (downloadParams) downloadMedia(downloadParams.id);
  }, [downloadParams]);

  useEffect(() => {
    if (configSuccess) {
      resetAddAndEditParams();
      setShowTransitionTimeOverlay(false);
      resetConfigAction();
      setDeleteParams(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configSuccess]);

  useEffect(() => {
    if (successful) {
      if (downloadParams) {
        // After downloading, reset the params and action without refreshing media or updating the configuration.
        setDownloadParams(null);
        resetAction();
        return;
      }

      if (mediaType.value === MediaTypes.ATTRACT_IMAGE) {
        onSlideActionSuccess();
      } else if (mediaType.value === MediaTypes.ATTRACT_VIDEO) {
        resetAddAndEditParams();
      }
      refreshMedia();
      resetAction();
      setDeleteParams(null);
      // updateConfig with nothing so the kiosk(s) fetch the updated media
      updateConfiguration({ id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successful]);

  useEffect(() => {
    if (configError || error) {
      setErrorMessage(configError || error);
      resetAction();
      resetConfigAction();
      setDeleteParams(null);
      setDownloadParams(null);
    }
  }, [configError, error]);

  const onSlideActionSuccess = () => {
    const attractLoopOrder = attractLoop && attractLoop.order ? attractLoop.order : [];

    let newOrder: string[];
    if (editParams) {
      const oldS3Name = (editParams as MediaResponse).s3Name;
      const newS3Name = (response as MediaResponse).s3Name;
      newOrder = attractLoopOrder.map((s3Name) => (s3Name === oldS3Name ? newS3Name : s3Name));
    } else if (deleteParams) {
      const oldS3Name = (deleteParams as MediaResponse).s3Name;
      newOrder = attractLoopOrder.filter((s3Name) => s3Name !== oldS3Name);
    } else {
      const newS3Name = (response as MediaResponse).s3Name;
      newOrder = attractLoopOrder.concat(newS3Name);
    }

    updateAttractLoopConfig({ order: newOrder });
    resetAddAndEditParams();
  };

  const onDeleteConfirm = () => {
    if (mediaType.value === MediaTypes.LINKED_CONTENT) {
      updateAttractLoopConfig({ linkedContentUrl: undefined });
    } else {
      deleteMedia((deleteParams as MediaResponse).id);
    }
  };

  const updateAttractLoopConfig = (payload: Partial<AttractLoop>) => {
    updateConfiguration({
      id,
      configuration: {
        ...configColumn,
        globalSettings: {
          ...globalSettings,
          attractLoop: { ...attractLoop, ...payload }
        }
      }
    });
  };

  const getMediaCount = (): number => {
    if (mediaType.value === MediaTypes.LINKED_CONTENT) {
      return attractLoop && attractLoop.linkedContentUrl ? 1 : 0;
    }
    return mediaRecords.length;
  };

  const resetAddAndEditParams = () => {
    setAddParams(false);
    setEditParams(null);
  };

  return (
    <>
      {/* LOADING OVERLAY */}
      {(pending || configPending) && (
        <Backdrop open={pending || configPending ? true : false}>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}

      {/* MAIN CONTENT */}
      <FlexBox direction="column" noWrap>
        <FlexBox align="center" margin="0 0 5" direction="row">
          <Typography fontSize={20} fontWeight={700} sx={{ marginRight: '20px' }}>
            <Box width={180}> Type </Box>
          </Typography>
          <SelectDropdown
            items={getSortedList(mediaTypeList, 'name')}
            itemToString={(item: MediaType) => item.name}
            selectedItem={mediaType}
            onChange={(selectedMedia) => setMediaType(selectedMedia)}
            radius="round"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: mediaType ? '#000' : '#9E9E9E'
              }
            }}
          />
        </FlexBox>
        {/* COMMON CONTROLS (Active, Slide Time, Create) */}
        <Controls
          configuration={configuration}
          mediaCount={getMediaCount()}
          mediaType={mediaType.value as MediaTypes}
          onAddClick={() => setAddParams(true)}
          onMakeActiveClick={() => updateAttractLoopConfig({ type: mediaType.value })}
          onSetTimeClick={() => setShowTransitionTimeOverlay(true)}
        />
        {/* SLIDESHOW TABLE */}
        {mediaType.value === MediaTypes.ATTRACT_IMAGE && (
          <MediaTable
            mediaRecords={sortedMediaRecords}
            mediaType={MediaTypes.ATTRACT_IMAGE}
            onDeleteClick={setDeleteParams}
            onDownloadClick={setDownloadParams}
            onEditClick={(mediaRecord) => setEditParams(mediaRecord as MediaResponse)}
            onPreviewClick={setImagePreviewSrc}
            onOrderChange={(order) => updateAttractLoopConfig({ order })}
          />
        )}
        {/* VIDEO TABLE */}
        {mediaType.value === MediaTypes.ATTRACT_VIDEO && (
          <MediaTable
            mediaRecords={mediaRecords}
            mediaType={MediaTypes.ATTRACT_VIDEO}
            onDeleteClick={setDeleteParams}
            onDownloadClick={setDownloadParams}
            onEditClick={() => setEditParams(true)}
          />
        )}
        {/* LINKED CONTENT TABLE */}
        {mediaType.value === MediaTypes.LINKED_CONTENT && (
          <LinkedContentTable
            linkedContentUrl={attractLoop ? attractLoop.linkedContentUrl : undefined}
            onDeleteClick={() => setDeleteParams(true)}
            onEditClick={() => setEditParams(true)}
          />
        )}
      </FlexBox>

      {/* DELETE OVERLAY */}
      {deleteParams && (
        <FormDialog
          cancelButtonAction={() => setDeleteParams(null)}
          cancelButtonText="Cancel"
          confirmButtonAction={onDeleteConfirm}
          confirmButtonText="Delete"
          open
        >
          <Typography fontSize={24} maxWidth="524px">
            {`Are you sure you want to delete this ${mediaTypeLabelLowercase}?`}
          </Typography>
        </FormDialog>
      )}

      {/* ADD/EDIT OVERLAYS */}
      {(addParams || editParams) && (
        <>
          {mediaType.value === MediaTypes.ATTRACT_IMAGE && (
            <ManageImageOverlay
              action={addParams ? 'Create' : 'Edit'}
              configurationId={id}
              halfPreview
              mediaRecord={editParams ? (editParams as MediaResponse) : undefined}
              mediaType={MediaTypes.ATTRACT_IMAGE}
              onClose={() => resetAddAndEditParams()}
            />
          )}
          {mediaType.value === MediaTypes.LINKED_CONTENT && (
            <ManageContentUrlOverlay
              action={addParams ? 'Select' : 'Edit'}
              configuration={configuration}
              onClose={() => resetAddAndEditParams()}
              onSave={updateAttractLoopConfig}
            />
          )}
          {mediaType.value === MediaTypes.ATTRACT_VIDEO && (
            <ManageVideoOverlay
              action={addParams ? 'Select' : 'Edit'}
              configuration={configuration}
              onClose={() => resetAddAndEditParams()}
            />
          )}
        </>
      )}

      {/* PREVIEW IMAGE OVERLAY */}
      {imagePreviewSrc && <PreviewImageOverlay imageSrc={imagePreviewSrc} onClose={() => setImagePreviewSrc(null)} />}

      {/* SLIDE TIME OVERLAY */}
      {showTransitionTimeOverlay && (
        <TransitionTimeOverlay
          configuration={configuration}
          onClose={() => setShowTransitionTimeOverlay(false)}
          onSave={updateAttractLoopConfig}
        />
      )}

      {/* ERROR OVERLAY */}
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage(null)}
          confirmButtonAction={() => setErrorMessage(null)}
          confirmButtonText="Ok"
          open
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
    </>
  );
};

export default AttractLoopLayout;
